import { ArrowDropDown, ArrowDropUp, Info, Refresh } from "@mui/icons-material";
import { Button, ClickAwayListener, Paper, Popper, Tooltip } from "@mui/material";
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useDeleteTriggerTestRun, useExecuteTestRunForNode } from "../api/nodeApi";
import { Node, NodeRunInstance, NodeTestRunInstance, RunInstance } from "../types/workflow";
import { CheckCircleIcon, LightingBolt, RunTestIcon, SolarArrowRightBroken } from "../svgUrls";
import clsx from "clsx";
import HorizontalTabs from "./Common/HorizontalTabs";
import "../styles/runTest.css";
import ThemedRadio from "./Common/ThemedRadio";
import { NodeConfig } from "../types/nodes";
import { extractNodeID } from "./extractNodeIDs";
import { formatDateTime } from "../utils/misc";
import { WorkflowServiceIcon } from './Common/WorkflowServiceIcon';

type RunTestProps = {
    node: Partial<Node>;
    isClickDisabled: boolean;
    showSelectRunId?: boolean;
    updateNodeConfigsFromRunInstances: (nodeId: string, response_config: string) => void;
    testRunInputs?: {run_instance: RunInstance, node_run_instances: NodeRunInstance[]}[];
    selectedRunInstanceId: string;
    setSelectedRunInstanceId: (value: string) => void;
    refetchTestRunInputs: (params: { nodeId: string, reset: boolean }, options?: { onSettled: () => void }) => void;
    refetchTestRunInstance: () => void;
    workflowType: string;
    hasTestData?: boolean;
    testRunInstance?: NodeTestRunInstance;
    testRunInstances?: Record<string, NodeTestRunInstance>;
    getNodeConfigsForNodeFromRef: (nodeId: string) => NodeConfig;
}

type RunTestHeaderProps = {
    showSelectRunId?: boolean;
    testRunInputs?: any[];
    hasTestData?: boolean;
    refetchTestRunInputs: (params: { nodeId: string, reset: boolean }, options?: { onSettled: () => void }) => void;
    workflowType?: string;
    nodeId: string;
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
}

const RunTestHeader: React.FC<RunTestHeaderProps> = ({
    showSelectRunId,
    testRunInputs,
    hasTestData,
    refetchTestRunInputs,
    workflowType,
    nodeId,
    isLoading,
    setIsLoading,
}) => {
    if (!showSelectRunId) return null;

    return (
        <div>
            {!testRunInputs?.length ? (
                <div className="flex items-center justify-center bg-backgroundBlue rounded-lg p-4 text-sm">
                    <div className="flex flex-col items-center gap-3">
                        <div className="text-greyishBlue">
                            {hasTestData ? 
                                "To confirm your trigger is set up correctly, we'll find a recent record in your account"
                            :
                                "No run instances available. Perform action to run this node and click here to refresh"
                            }
                        </div>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                                setIsLoading(true);
                                refetchTestRunInputs({ nodeId: nodeId, reset: false }, { onSettled: () => {
                                    setIsLoading(false);
                                } });
                            }}
                            className="!bg-nanonetsBlue !normal-case !text-white hover:!bg-nanonetsBlueHover"
                            startIcon={<RunTestIcon className={isLoading ? "testRunIconRotate" : ""} />}
                        >
                            Test trigger
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center flex-col">
                    <div>We can select from recent node trigger runs</div>
                    <div>
                        to test rest of the actions in the workflow with
                    </div>
                </div>
            )}
        </div>
    );
};

type RunTestResultsProps = {
    showResult: boolean;
    testRunSuccessful: boolean;
    timeRanAt: string;
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
    testRunResultsInput: Record<string, string>;
    testRunResultsOutput: Record<string, string>;
}

const RunTestResults: React.FC<RunTestResultsProps> = ({
    showResult,
    testRunSuccessful,
    timeRanAt,
    selectedTab,
    setSelectedTab,
    testRunResultsInput,
    testRunResultsOutput
}) => {
    const currentResults = selectedTab === "inputs" ? testRunResultsInput : testRunResultsOutput;

    return (
        <div className="mt-4">
            {showResult ? (
                testRunSuccessful ? (
                <div className="flex items-center gap-2 p-2 bg-[#f1f8f2] rounded text-xs">
                    <CheckCircleIcon />
                    <strong>Test ran successfully</strong> {timeRanAt}
                    </div>
                ) : (
                    <div className="flex items-center gap-2 py-1 px-2 bg-[#fcf2f1] rounded text-xs">
                        <Info fontSize="small" htmlColor="#db584e" />
                        <strong>Test failed to run</strong> {timeRanAt}
                    </div>
                )
            ) : null}
            <HorizontalTabs
                tabArray={[
                    { id: "inputs", value: "Inputs" },
                    { id: "outputs", value: "Outputs" }
                ]}
                tabValue={selectedTab}
                setTabValue={setSelectedTab}
            />
            <div>
                {Object.entries(currentResults)?.length ? (
                    Object.entries(currentResults).map(([dataKey, dataValue]) => (
                        <div className="my-4" key={dataKey}>
                            <div className="text-2xs text-greyishBlue mb-2">
                                {dataKey}
                            </div>
                            <div className="p-3 text-xs bg-backgroundGrey max-h-[140px] overflow-auto rounded text-wrap break-words">
                                {JSON.stringify(dataValue)}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="border border-solid border-borderGrey text-xs flex items-center gap-2 mt-2 p-2 rounded">
                        <Info fontSize="small" htmlColor="#7e86a0" />
                        No {selectedTab} to display
                    </div>
                )}
            </div>
        </div>
    );
};

const WorkflowNodeIcons: React.FC<{ workflowType: string }> = ({ workflowType }) => {


    return (
        <div className="flex gap-2 items-center">
            <WorkflowServiceIcon
                workflowType={workflowType}
                className="border border-solid border-lightGrey p-2 rounded-lg"
                width={40}
                height={40}
            />
            <SolarArrowRightBroken />
            <LightingBolt 
                height={40} 
                width={40} 
                className="border border-solid border-lightGrey p-1 rounded-lg" 
            />
        </div>
    );
};

type RunTestButtonProps = {
    isTestRunning: boolean;
    getResults: () => void;
    setIsTextFieldFocused: (value: boolean) => void;
}

const RunTestButton: React.FC<RunTestButtonProps> = ({ 
    isTestRunning, 
    getResults, 
    setIsTextFieldFocused 
}) => {
    return (
        <div className="flex-1 flex justify-center">
            <Button 
                variant="contained"
                startIcon={<RunTestIcon className={isTestRunning ? "testRunIconRotate" : ""} />}
                size="small"
                onClick={() => {
                    getResults();
                    setIsTextFieldFocused(false);
                }}
                className={clsx("!bg-nanonetsBlue !normal-case !text-white")}
            >
                Run test
            </Button>
        </div>
    );
};

const RunInstanceList: React.FC<{
    testRunInputs: any[];
    selectedRunInstanceId: string;
    setSelectedRunInstanceId: (id: string) => void;
    updateNodeConfigsFromRunInstances: (nodeId: string, response_config: string) => void;
    node: Partial<Node>;
}> = ({
    testRunInputs,
    selectedRunInstanceId,
    setSelectedRunInstanceId,
    updateNodeConfigsFromRunInstances,
    node
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    if (!testRunInputs?.length) return null;

    const handleClick = (event: React.MouseEvent<HTMLDivElement>, 
        runInstance: RunInstance, 
        nodeRunInstances: NodeRunInstance[]) => {
        setAnchorEl(event.currentTarget);
        setSelectedRunInstanceId(runInstance.id);
        updateNodeConfigsFromRunInstances(node.id || '', nodeRunInstances?.[0]?.flat_map || '');
        setTimeout(() => {
            setShowDetails(true);
        }, 0);
    };

    const handleClickAway = () => {
        setShowDetails(false);
    };

    const selectedTestRunInstance = testRunInputs.find(input => input.run_instance.id === selectedRunInstanceId)?.node_run_instances?.[0];

    return (
        <>
            <div className="w-full">
                {testRunInputs.map((testRunInput) => {
                    const runInstance = testRunInput.run_instance;
                    const nodeRunInstances = testRunInput.node_run_instances;
                    return (
                        <div 
                            key={runInstance.id}
                            className="border border-solid border-borderGrey rounded mb-2 p-4 cursor-pointer hover:bg-backgroundBlue"
                            onClick={(e) => handleClick(e, runInstance, nodeRunInstances)}
                        >
                            <div className="flex items-center gap-2">
                                <ThemedRadio
                                    color="primary"
                                    size="small"
                                    checked={runInstance.id === selectedRunInstanceId}
                                />
                                <div>
                                    <div className="text-sm">Record {String.fromCharCode(65 + testRunInputs.indexOf(testRunInput))}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <Popper
                open={showDetails}
                anchorEl={anchorEl}
                placement="left-start"
                style={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper className="p-4 shadow-lg max-w-[400px]">
                        <SelectedRunInstanceDetails 
                            selectedNodeRunInstance={selectedTestRunInstance}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

type SelectedRunInstanceDetailsProps = {
    selectedNodeRunInstance: NodeRunInstance | null;
}

const SelectedRunInstanceDetails: React.FC<SelectedRunInstanceDetailsProps> = ({
    selectedNodeRunInstance
}) => {
    return (
        <div className="text-greyishBlue overflow-hidden">
            <div className="truncate">
                Showing data for Run ID
                <Tooltip title={selectedNodeRunInstance?.run?.id}>
                    <span className="text-black">{" "}{selectedNodeRunInstance?.run?.id}</span>
                </Tooltip>
            </div>
            <div className="mb-2">
                Run on {formatDateTime(selectedNodeRunInstance?.run?.created_time || '')}
            </div>
            <div className="overflow-auto max-h-[336px] h-full rounded border border-solid border-lightGrey">
                <table className="border-spacing-0 border-separate">
                    {Object.entries(JSON.parse(selectedNodeRunInstance?.flat_map || '{}')).map(([key, value]) => (
                        <tr key={key}>
                            <td className="p-1 border-b border-r border-solid border-lightGrey">
                                <div className="rounded p-1 border border-solid border-lightGrey bg-backgroundGreyLight">{key}</div>
                            </td>
                            <td className="p-1 border-b border-solid border-lightGrey">{String(value)}</td>
                        </tr>
                    ))}
                </table>
            </div>
        </div>
    );
};

const substituteNodeRunConfig = (
    nodeRunConfig: Record<string, string>,
    testRunInstances: Record<string, NodeTestRunInstance> | undefined,
    triggerTestRunInstance: NodeRunInstance | undefined
) => {
    if (!nodeRunConfig) return nodeRunConfig;

    const substitutedConfig: Record<string, string> = {};

    // Process each key-value pair in nodeRunConfig
    Object.entries(nodeRunConfig).forEach(([key, configValue]) => {
        if (!configValue) {
            substitutedConfig[key] = configValue;
            return;
        }
        configValue = String(configValue);

        let substitutedValue = configValue;

        // Split by template literals while preserving them
        const parts = configValue.split(/(\{\{[^}]+\}\})/g);

        parts.forEach(part => {
            if (part.startsWith("{{") && part.endsWith("}}")) {
                const { nodeId, dataPath } = extractNodeID(part) || {};
                
                if (!nodeId || !dataPath) return;

                let valueToSubstitute;
                
                // If nodeId matches trigger node, use triggerTestRunInstance
                if (triggerTestRunInstance && nodeId === triggerTestRunInstance.node.id) {
                    try {
                        const flatMap = JSON.parse(triggerTestRunInstance.flat_map || '{}');
                        valueToSubstitute = flatMap[dataPath];
                    } catch (e) {
                        console.error('Error parsing trigger test run instance flat_map:', e);
                    }
                } 
                // Otherwise look in testRunInstances
                else if (testRunInstances && testRunInstances[nodeId]) {
                    try {
                        const flattenedOutput = JSON.parse(testRunInstances[nodeId].flattened_output || '{}');
                        valueToSubstitute = flattenedOutput[dataPath];
                    } catch (e) {
                        console.error('Error parsing test run instance flattened_output:', e);
                    }
                }

                if (valueToSubstitute !== undefined) {
                    substitutedValue = substitutedValue.replace(part, valueToSubstitute);
                }
            }
        });

        substitutedConfig[key] = substitutedValue;
    });

    return substitutedConfig;
};

const RunTest = ({
    node,
    isClickDisabled,
    showSelectRunId,
    updateNodeConfigsFromRunInstances,
    testRunInputs,
    selectedRunInstanceId,
    setSelectedRunInstanceId,
    refetchTestRunInputs,
    refetchTestRunInstance,
    workflowType,
    hasTestData,
    testRunInstance,
    testRunInstances,
    getNodeConfigsForNodeFromRef,
}: RunTestProps) => {
    const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
    // const [testRunResultsInput, setTestRunResultsInput] = useState<Record<string, string>>({});
    const [testRunResultsOutput, setTestRunResultsOutput] = useState<Record<string, string>>({});
    const [testRunSuccessful, setTestRunSuccessful] = useState(false);
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [selectedTab, setSelectedTab] = useState("inputs");
    const [timeRanAt, setTimeRanAt] = useState('');
    const { mutate: executeTestRun } = useExecuteTestRunForNode();
    const [showResult, setShowResult] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);

    const nodeRunConfig = getNodeConfigsForNodeFromRef(node.id || '')?.run_config;
    const nodeRunConfigMetadata = getNodeConfigsForNodeFromRef(node.id || '')?.run_config_metadata;

    const triggerTestRunInstance = testRunInputs?.find(input => input.run_instance.id === selectedRunInstanceId)?.node_run_instances?.[0];

    const nodeRunConfigSubstituted = substituteNodeRunConfig(nodeRunConfig, testRunInstances, triggerTestRunInstance);

    const nodeRunConfigFiltered = Object.entries(nodeRunConfigSubstituted || {}).reduce((acc, [key, value]) => {
        if (nodeRunConfigMetadata?.[key]?.type !== "hidden") {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, string>);

    // const testRunResultsInput = nodeRunConfigFiltered;
    const [testRunResultsInput, setTestRunResultsInput] = useState<Record<string, string>>(nodeRunConfigFiltered);

    useEffect(() => {
        // Reset component state when node id changes
        // setTestRunResultsInput({});
        setTestRunResultsInput(nodeRunConfigFiltered);
        setTestRunResultsOutput({});
        setTestRunSuccessful(false);
        setIsTestRunning(false);
        setSelectedTab("inputs");
        setTimeRanAt('');
        setIsTextFieldFocused(false);
        setShowResult(false);
    }, [node.id]);

    const getInput = () => {
        if(!isTextFieldFocused && node.id) refetchTestRunInputs({ nodeId: node.id, reset: false });
        setIsTextFieldFocused(!isTextFieldFocused);
    }

    const [isLoading, setIsLoading] = useState(false);

    const resetTestRecords = async () => {
        setIsLoading(true);
        refetchTestRunInputs({ nodeId: node.id || "", reset: true }, { onSettled: () => {
            setIsLoading(false);
        } });
    }
    
    useEffect(() => {
        if (testRunInstance) {
            const { error, input_data, output_data, flattened_output, message } = testRunInstance;
            if (error) {
                setTestRunSuccessful(false);
                setTestRunResultsOutput({ error });
            } else {
                setTestRunSuccessful(true);
                try {
                    if (input_data) {
                        setTestRunResultsInput(JSON.parse(input_data));
                    }
                    if (output_data) {
                        setTestRunResultsOutput(JSON.parse(output_data));
                    }
                } catch (e) {
                    setTestRunSuccessful(false);
                    setTestRunResultsOutput({ err: message || "No result found on node execution" });
                    console.log("getResults", e);
                }
            }
        }
    }, [testRunInstance]);

    const getResults = () => {
        setIsTestRunning(true);
        const timeRanAt = new Date();
        setTimeRanAt(formatDateTime(timeRanAt.toISOString()));
        executeTestRun({ runInstanceId: selectedRunInstanceId, node }, {
            onSuccess: () => {
                setShowResult(true);
                refetchTestRunInstance();
            },
            onSettled: () => setIsTestRunning(false)
        });
    }

    return (
        <>
            <WorkflowNodeIcons workflowType={workflowType} />
            <RunTestHeader 
                showSelectRunId={showSelectRunId}
                testRunInputs={testRunInputs}
                hasTestData={hasTestData}
                refetchTestRunInputs={refetchTestRunInputs}
                workflowType={workflowType}
                nodeId={node.id || ""}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
            />
            <div className="w-full">
                {showSelectRunId && (
                    <RunInstanceList
                        testRunInputs={testRunInputs ?? []}
                        selectedRunInstanceId={selectedRunInstanceId}
                        setSelectedRunInstanceId={setSelectedRunInstanceId}
                        updateNodeConfigsFromRunInstances={updateNodeConfigsFromRunInstances}
                        node={node}
                    />
                )}
                {!showSelectRunId && (
                    <RunTestButton 
                        isTestRunning={isTestRunning}
                        getResults={getResults}
                        setIsTextFieldFocused={setIsTextFieldFocused}
                    />
                )}
                {!showSelectRunId && (
                    <RunTestResults 
                        showResult={showResult}
                        testRunSuccessful={testRunSuccessful}
                        timeRanAt={timeRanAt}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        testRunResultsInput={testRunResultsInput}
                        testRunResultsOutput={testRunResultsOutput}
                    />
                )}
            </div>
            {showSelectRunId && testRunInputs?.length ? (
                <div className="flex justify-between mb-4 w-full">
                    <Button 
                        variant="outlined" 
                        onClick={resetTestRecords} 
                        className="!text-nanonetsBlue border-nanonetsBlue hover:bg-nanonetsBlue hover:text-white w-full"
                >
                    Reset Test Records
                    </Button>
                </div>
            ) : null}
        </>
    )
}

export default RunTest;